import React, { useState, useRef, ReactNode } from 'react';
import { Swiper, SwiperSlide, SwiperRef, SwiperProps } from 'swiper/react';
import 'swiper/css';
import classNames from 'classnames';

import { ReactComponent as Corner } from '../../assets/images/PaginationCorner.svg';
import styles from './styles.module.scss';

interface ISwiperSliderProps extends SwiperProps {
  children: ReactNode[];
  variant: 'metro' | 'fragments';
}

export const SwiperSlider: React.FC<ISwiperSliderProps> = ({
  children,
  spaceBetween = 30,
  slidesPerView = 1,
  onSlideChange,
  loop = true,
  variant,
  ...props
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const swiperRef = useRef<SwiperRef | null>(null);

  const handleMarkerClick = (index: number): void => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideToLoop(index);
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  return (
    <>
      <Swiper
        ref={swiperRef}
        spaceBetween={spaceBetween}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
          if (onSlideChange) onSlideChange(swiper);
        }}
        {...props}
        className={classNames(styles.swiper, props.className)}
        loop={loop}
        onClick={() => {
          if (swiperRef.current) {
            swiperRef.current.swiper.slideNext();
          }
        }}
      >
        {children.map((child, index) => (
          <SwiperSlide key={index}>{child}</SwiperSlide>
        ))}
      </Swiper>

      {variant === 'fragments' && (
        <div className={styles.buttons}>
          <button className={classNames(styles.button, styles.next)} onClick={handlePrev} type="button">
            <div className={classNames('ui-icon-ArrowLeft', styles.icon)} />
          </button>

          <button className={classNames(styles.button, styles.prev)} onClick={handleNext} type="button">
            <div className={classNames('ui-icon-ArrowRight', styles.icon)} />
          </button>
        </div>
      )}

      <div className={classNames(styles.pagination, { [styles[variant]]: Boolean(variant) })}>
        {children.map((_, index) => (
          <div
            key={index}
            className={classNames(styles.container, { [styles.active]: index === activeIndex })}
            onClick={() => handleMarkerClick(index)}
          >
            <Corner fill="currentColor" className={styles.corner} />
            <div className={styles.marker}></div>
          </div>
        ))}
      </div>
    </>
  );
};
